import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component, inject, OnDestroy, OnInit,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';

import { AuthService, ExternalLoginComponent } from '@instaclause-webapp/shared-core';
import {
  ButtonComponent,
  CheckboxComponent,
  EmailFieldComponent,
  PasswordFieldComponent,
  ToastComponent,
  ToastService,
  ToastType,
} from '@instaclause-webapp/shared-ui';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import {
  catchError, firstValueFrom, of, Subscription,
} from 'rxjs';

import { MenuComponent } from '../../ui/menu/menu.component';

@Component({
  selector: 'app-login',
  imports: [
    CommonModule,
    MenuComponent,
    RouterModule,
    TranslocoModule,
    ReactiveFormsModule,
    EmailFieldComponent,
    PasswordFieldComponent,
    CheckboxComponent,
    ButtonComponent,
    ExternalLoginComponent,
    ToastComponent,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnInit, OnDestroy {
  rememberMe = true;
  isLoggingIn = false;
  loginForm = inject(FormBuilder).group({
    email: [ '', Validators.required ],
    password: [ '', Validators.required ],
  });

  private authService = inject(AuthService);
  private toastService = inject(ToastService);
  private router = inject(Router);
  private translocoService = inject(TranslocoService);
  private subscription = new Subscription();

  ngOnInit() {
    this.subscription.add(
      this.authService.user$.subscribe((user) => {
        if (user) {
          this.router.navigate([ './' ]);
        }
      })
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  async login() {
    if (this.isLoggingIn) return;

    if (!this.loginForm.valid) {
      this.loginForm.markAllAsTouched();

      return;
    }

    const { email, password } = this.loginForm.value;
    if (!email || !password) return;

    this.isLoggingIn = true;
    try {
      await firstValueFrom(
        this.authService.login(email, password, this.rememberMe).pipe(
          catchError((error: HttpErrorResponse) => {
            const isInvalidCredentials = error.status === 401 || error.status === 400;
            this.toastService.showToast({
              type: ToastType.Error,
              text: this.translocoService.translate(
                isInvalidCredentials ? 'LOGIN.ERROR' : 'LOGIN.GENERIC_ERROR'
              ),
              duration: 5,
              testId: 'login-error-toast',
            });

            return of(null);
          })
        )
      );
    } finally {
      this.isLoggingIn = false;
    }
  }

  onGoogleLoginFailed() {
    this.toastService.showToast({
      type: ToastType.Error,
      text: this.translocoService.translate('LOGIN.ERROR'),
      duration: 5,
      testId: 'login-error-toast',
    });
  }
}
